import { useEffect, useState, type ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { aplicarVersionNueva, useHayVersionNueva } from '../store/actualizacion';
import { useFocusOpen } from './foco-estado';
import { PantallaDeCarga } from '../ui/carga';

/**
 * «HAY UNA VERSIÓN NUEVA.» El aviso que `registerType: 'prompt'` exige y que no
 * existía: sin él, la versión nueva esperaba sin que nadie pudiera aceptarla y
 * solo entraba cuando se cerraban todas las pestañas de la app.
 *
 * NO BLOQUEA NADA. Es un `status`, no un diálogo: la app sigue funcionando con
 * la versión que tiene, y actualizar es una decisión del usuario. Y NO APARECE
 * DURANTE UN FOCO —ni corriendo ni en pausa—: actualizar recarga la página, y una
 * recarga a mitad de un pomodoro es justo lo que el modo `prompt` existe para
 * evitar.
 */
export function AvisoVersion(): ReactElement | null {
  const hay = useHayVersionNueva();
  const enFoco = useFocusOpen();
  const [actualizando, setActualizando] = useState(false);
  useRutaDeAvisos();
  // Tocado «Actualizar», la app ya se está yendo: la carga la tapa hasta que
  // recarga, en vez de dejar la pantalla vieja a medio desmontar.
  if (actualizando) return <PantallaDeCarga />;
  if (!hay || enFoco) return null;

  return (
    <div className="aviso-version glass-2" role="status">
      <span className="t-sm">Hay una versión nueva.</span>
      <button
        type="button"
        className="btn btn--sm btn--primary"
        onClick={() => {
          setActualizando(true);
          aplicarVersionNueva();
        }}
      >
        Actualizar
      </button>
    </div>
  );
}

/**
 * Al tocar una notificación con la app abierta, el service worker no navega la
 * ventana —eso la recargaría entera, foco incluido—: le manda la ruta y la
 * navegación la hace el router, sin recargar. Vive aquí porque es el otro punto
 * de contacto entre el service worker y la app.
 */
function useRutaDeAvisos(): void {
  const router = useRouter();
  useEffect(() => {
    const contenedor = navigator.serviceWorker;
    if (!contenedor) return;
    const alMensaje = (evento: MessageEvent) => {
      const datos = evento.data as { type?: string; ruta?: unknown } | null;
      if (datos?.type !== 'NAVEGAR' || typeof datos.ruta !== 'string') return;
      // Solo rutas de la propia app: nada de `//otro-sitio`.
      if (!datos.ruta.startsWith('/') || datos.ruta.startsWith('//')) return;
      router.history.push(datos.ruta);
    };
    contenedor.addEventListener('message', alMensaje);
    return () => contenedor.removeEventListener('message', alMensaje);
  }, [router]);
}
